/**
 * `/$org/skills`: the organization's skills as a leaderboard, after skills.sh —
 * a filter column (Browse, Source, Topics) beside a ranked list (#, skill and
 * slug, owner, loads over the last eight weeks, a weekly sparkline), with
 * All / Most used / Recent tabs and a search field that "/" focuses.
 *
 * The board comes from `/api/amoreg/skill-board` (state/skillBoard): the
 * agent's own skills and the organization's library, each with the times the
 * organization's chats loaded it. Ranking and filtering are pure
 * (skillBoardLogic). A row opens the skill's page.
 *
 * @module components/skills/SkillsPage
 */
import { Link } from "@tanstack/react-router";
import { PlusIcon, RefreshCwIcon, SearchIcon } from "lucide-react";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";

import { useStartChatWithText } from "../../hooks/useStartChatWithText";
import { cn } from "../../lib/utils";
import { orgUrlSegment } from "../../orgRouting";
import { useActiveOrg } from "../../state/orgs";
import { useSkillBoard, type SkillBoardEntry } from "../../state/skillBoard";
import { CREATE_SKILL_PROMPT, SKILL_FILTERS, type SkillFilter } from "../connections/skillContent";
import { Button } from "../ui/button";
import { InputGroup, InputGroupAddon, InputGroupInput } from "../ui/input-group";
import { Kbd } from "../ui/kbd";
import { ScrollArea } from "../ui/scroll-area";
import {
  formatUses,
  NO_SKILL_FILTERS,
  rankSkills,
  SKILL_VIEWS,
  skillTopics,
  sourceCounts,
  type RankedSkill,
  type SkillBoardFilters,
} from "./skillBoardLogic";
import { SkillSparkline } from "./SkillSparkline";

const SOURCE_LABEL: Record<SkillFilter, string> = {
  all: "All sources",
  system: "System",
  organization: "Organisation",
  jurisdiction: "Jurisdiction",
};

/** True when a key press lands in a field, so "/" types instead of jumping to search. */
const isTyping = (target: EventTarget | null) =>
  target instanceof HTMLElement &&
  (target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName));

function FilterGroup({
  title,
  children,
}: {
  readonly title: string;
  readonly children: ReactNode;
}) {
  return (
    <div>
      <h2 className="px-2.5 pb-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground/80">
        {title}
      </h2>
      <ul className="flex flex-col gap-px">{children}</ul>
    </div>
  );
}

function FilterItem({
  active,
  count,
  onClick,
  children,
}: {
  readonly active: boolean;
  readonly count?: number | undefined;
  readonly onClick: () => void;
  readonly children: ReactNode;
}) {
  return (
    <li>
      <button
        type="button"
        aria-pressed={active}
        onClick={onClick}
        className={cn(
          "flex h-8 w-full cursor-pointer items-center gap-2 rounded-lg px-2.5 text-left text-[13px] outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring",
          active
            ? "bg-foreground/[0.07] font-medium text-foreground"
            : "text-muted-foreground hover:bg-foreground/[0.04] hover:text-foreground",
        )}
      >
        <span className="min-w-0 flex-1 truncate">{children}</span>
        {count !== undefined ? (
          <span className="shrink-0 text-[12px] tabular-nums text-muted-foreground/70">
            {count}
          </span>
        ) : null}
      </button>
    </li>
  );
}

/** Narrow screens: the same filters as one scrolling row of chips. */
function FilterChip({
  active,
  onClick,
  children,
}: {
  readonly active: boolean;
  readonly onClick: () => void;
  readonly children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        "inline-flex h-7 shrink-0 cursor-pointer items-center rounded-full border px-3 text-[12px] outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring",
        active
          ? "border-foreground/20 bg-foreground/[0.07] font-medium text-foreground"
          : "border-border/70 text-muted-foreground hover:text-foreground",
      )}
    >
      {children}
    </button>
  );
}

const ROW_GRID =
  "grid grid-cols-[2rem_minmax(0,1fr)_auto] items-center gap-x-4 @[44rem]:grid-cols-[2.5rem_minmax(0,1fr)_minmax(0,12rem)_5.5rem_5rem]";

function LeaderboardRow({
  row,
  orgSegment,
}: {
  readonly row: RankedSkill;
  readonly orgSegment: string;
}) {
  const { entry } = row;
  return (
    <li>
      <Link
        to="/$org/skills/$slug"
        params={{ org: orgSegment, slug: entry.slug }}
        data-skill-row={entry.slug}
        className={cn(
          ROW_GRID,
          "group rounded-xl px-3 py-3 outline-none transition-colors hover:bg-foreground/[0.035] focus-visible:ring-2 focus-visible:ring-ring",
        )}
      >
        <span className="font-mono text-[13px] tabular-nums text-muted-foreground/80">
          {row.rank}
        </span>
        <span className="min-w-0">
          <span className="flex min-w-0 items-baseline gap-2">
            <span className="max-w-full shrink-0 truncate text-[14px] font-medium text-foreground">
              {entry.name}
            </span>
            <span className="hidden min-w-0 truncate font-mono text-[12px] text-muted-foreground/70 @[32rem]:inline">
              {entry.slug}
            </span>
          </span>
          {/* Narrow: the owner sits under the name, as skills.sh's mobile list. */}
          <span className="mt-0.5 block truncate text-[12px] text-muted-foreground @[44rem]:hidden">
            {row.owner}
          </span>
        </span>
        <span className="hidden min-w-0 truncate text-[13px] text-muted-foreground @[44rem]:block">
          {row.owner}
        </span>
        <span className="text-right text-[14px] font-medium tabular-nums text-foreground">
          {formatUses(entry.uses)}
        </span>
        <span className="hidden justify-end @[44rem]:flex">
          <SkillSparkline values={entry.weekly} />
        </span>
      </Link>
    </li>
  );
}

function emptyCopy(filters: SkillBoardFilters): string {
  if (filters.query.trim())
    return `No skills match “${filters.query.trim()}”. Try another search or filter.`;
  if (filters.view === "most-used") return "No skill in this view was used in the last 8 weeks.";
  if (filters.view === "recent") return "No library skill in this view has an added date yet.";
  return "No skills in this view yet. Try another filter.";
}

export function SkillsPage() {
  const org = useActiveOrg();
  const orgSegment = org ? orgUrlSegment(org.id) : null;
  const isAdmin = org !== null && org.role !== "member";
  const startChat = useStartChatWithText(org?.id ?? null);
  const { result, isRefreshing, refresh } = useSkillBoard(org?.id ?? null);
  const board = result?.kind === "ok" ? result.board : null;
  const entries: ReadonlyArray<SkillBoardEntry> = board?.skills ?? [];
  const [filters, setFilters] = useState<SkillBoardFilters>(NO_SKILL_FILTERS);
  const search = useRef<HTMLInputElement>(null);

  // "/" jumps to the search field, as on skills.sh.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (
        event.key !== "/" ||
        event.metaKey ||
        event.ctrlKey ||
        event.altKey ||
        isTyping(event.target)
      )
        return;
      event.preventDefault();
      search.current?.focus();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const rows = useMemo(
    () => rankSkills(entries, filters, org?.name),
    [entries, filters, org?.name],
  );
  const topics = useMemo(() => skillTopics(entries), [entries]);
  const sources = useMemo(() => sourceCounts(entries), [entries]);
  const totalUses = useMemo(() => entries.reduce((sum, entry) => sum + entry.uses, 0), [entries]);
  const set = (patch: Partial<SkillBoardFilters>) =>
    setFilters((current) => ({ ...current, ...patch }));

  const createSkill = () => {
    if (startChat) void startChat(CREATE_SKILL_PROMPT);
  };

  return (
    <ScrollArea className="h-full">
      <div
        data-skills-page=""
        className="@container mx-auto flex w-full max-w-[1240px] flex-col gap-6 px-4 pb-16 pt-14 sm:px-8 sm:pt-8"
      >
        <header className="flex flex-wrap items-end justify-between gap-x-6 gap-y-4">
          <div className="min-w-0">
            <h1 className="text-balance text-[28px] font-semibold leading-tight tracking-[-0.02em] text-foreground">
              Skills
            </h1>
            <p className="mt-1 max-w-xl text-[13px] text-muted-foreground">
              The methods Amoreg follows when a task calls for one, ranked by how often your
              organization&rsquo;s chats used each in the last 8 weeks.
            </p>
          </div>
          <div className="flex w-full flex-wrap items-center gap-2 @[44rem]:w-auto">
            <InputGroup className="h-8 min-w-0 flex-1 @[44rem]:w-64 @[44rem]:flex-none">
              <InputGroupAddon className="text-muted-foreground">
                <SearchIcon className="size-3.5" />
              </InputGroupAddon>
              <InputGroupInput
                ref={search}
                aria-label="Search skills"
                data-skills-search=""
                placeholder="Search skills…"
                className="text-sm"
                value={filters.query}
                onChange={(event) => set({ query: event.target.value })}
                onKeyDown={(event) => {
                  if (event.key === "Escape") {
                    set({ query: "" });
                    event.currentTarget.blur();
                  }
                }}
              />
              <InputGroupAddon align="inline-end" className="pe-2">
                <Kbd className="h-5 min-w-5 text-[11px]">/</Kbd>
              </InputGroupAddon>
            </InputGroup>
            {isAdmin && startChat ? (
              <Button
                size="sm"
                variant="outline"
                className="gap-1.5"
                onClick={createSkill}
                title="Draft a skill in chat"
              >
                <PlusIcon aria-hidden className="size-3.5" />
                Create skill
              </Button>
            ) : null}
          </div>
        </header>

        <div className="grid gap-x-10 gap-y-5 @[56rem]:grid-cols-[13rem_minmax(0,1fr)]">
          {/* The filter column, as skills.sh's: Browse, Source, Topics. */}
          <nav
            aria-label="Filter skills"
            className="hidden flex-col gap-6 @[56rem]:flex"
            data-skills-filters=""
          >
            <FilterGroup title="Browse">
              {SKILL_VIEWS.map((view) => (
                <FilterItem
                  key={view.id}
                  active={filters.view === view.id}
                  onClick={() => set({ view: view.id })}
                >
                  {view.label}
                </FilterItem>
              ))}
            </FilterGroup>
            <FilterGroup title="Source">
              {SKILL_FILTERS.map((source) => (
                <FilterItem
                  key={source.id}
                  active={filters.source === source.id}
                  count={board ? sources[source.id] : undefined}
                  onClick={() => set({ source: source.id })}
                >
                  {SOURCE_LABEL[source.id]}
                </FilterItem>
              ))}
            </FilterGroup>
            {topics.length > 0 ? (
              <FilterGroup title="Topics">
                <FilterItem active={filters.topic === null} onClick={() => set({ topic: null })}>
                  All topics
                </FilterItem>
                {topics.map((topic) => (
                  <FilterItem
                    key={topic.name}
                    active={filters.topic === topic.name}
                    count={topic.count}
                    onClick={() => set({ topic: filters.topic === topic.name ? null : topic.name })}
                  >
                    {topic.name}
                  </FilterItem>
                ))}
              </FilterGroup>
            ) : null}
          </nav>

          <section aria-label="Skill leaderboard" className="min-w-0" data-skills-leaderboard="">
            {/* Narrow screens: source and topic chips in place of the column. */}
            <div className="-mx-4 mb-3 flex gap-1.5 overflow-x-auto px-4 pb-1 @[56rem]:hidden">
              {SKILL_FILTERS.map((source) => (
                <FilterChip
                  key={source.id}
                  active={filters.source === source.id}
                  onClick={() => set({ source: source.id })}
                >
                  {source.id === "all" ? "All" : SOURCE_LABEL[source.id]}
                </FilterChip>
              ))}
              {topics.map((topic) => (
                <FilterChip
                  key={topic.name}
                  active={filters.topic === topic.name}
                  onClick={() => set({ topic: filters.topic === topic.name ? null : topic.name })}
                >
                  {topic.name}
                </FilterChip>
              ))}
            </div>

            <div className="flex items-end justify-between gap-4 border-b border-border/60">
              <div role="tablist" aria-label="Rank skills by" className="-mb-px flex gap-5">
                {SKILL_VIEWS.map((view) => (
                  <button
                    key={view.id}
                    type="button"
                    role="tab"
                    aria-selected={filters.view === view.id}
                    data-skills-tab={view.id}
                    onClick={() => set({ view: view.id })}
                    className={cn(
                      "cursor-pointer border-b-2 pb-2.5 text-[13px] outline-none transition-colors focus-visible:text-foreground",
                      filters.view === view.id
                        ? "border-foreground font-medium text-foreground"
                        : "border-transparent text-muted-foreground hover:text-foreground",
                    )}
                  >
                    {view.tab}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-1 pb-1.5">
                <span role="status" className="text-[12px] tabular-nums text-muted-foreground">
                  {board
                    ? `${rows.length} ${rows.length === 1 ? "skill" : "skills"} · ${formatUses(totalUses)} ${totalUses === 1 ? "use" : "uses"}`
                    : null}
                </span>
                <Button
                  variant="ghost-muted"
                  size="icon-xs"
                  title="Refresh"
                  aria-label="Refresh the leaderboard"
                  disabled={isRefreshing}
                  onClick={refresh}
                >
                  <RefreshCwIcon
                    className={cn("size-3.5", isRefreshing && board && "motion-safe:animate-spin")}
                  />
                </Button>
              </div>
            </div>

            <div
              aria-hidden
              className={cn(
                ROW_GRID,
                "px-3 pb-1 pt-3 text-[11px] font-medium uppercase tracking-wider text-muted-foreground/70",
              )}
            >
              <span>#</span>
              <span>Skill</span>
              <span className="hidden @[44rem]:block">Owner</span>
              <span className="text-right">
                Uses<span className="hidden @[44rem]:inline"> · 8 wk</span>
              </span>
              <span className="hidden text-right @[44rem]:block">Trend</span>
            </div>

            {result === null ? (
              <ul role="status" aria-label="Loading skills" className="flex flex-col gap-1">
                {Array.from({ length: 6 }, (_, index) => (
                  <li key={index} className="h-[52px] animate-pulse rounded-xl bg-muted/50" />
                ))}
              </ul>
            ) : result.kind !== "ok" ? (
              <div className="mt-2 rounded-2xl border border-border/60 bg-card px-6 py-8">
                <p className="text-[15px] font-medium text-foreground">
                  {result.kind === "unavailable"
                    ? "Skill usage isn’t available on this server"
                    : result.kind === "forbidden"
                      ? "You no longer have access to this organisation"
                      : "The leaderboard didn’t load"}
                </p>
                <p className="mt-1 text-sm text-muted-foreground">
                  {result.kind === "error"
                    ? "Try again in a moment."
                    : "The skill library is still listed under Plugins."}
                </p>
                {result.kind === "error" ? (
                  <Button size="sm" variant="outline" className="mt-4" onClick={refresh}>
                    Try again
                  </Button>
                ) : null}
              </div>
            ) : rows.length === 0 ? (
              <p className="px-3 py-10 text-sm text-muted-foreground">{emptyCopy(filters)}</p>
            ) : orgSegment ? (
              <ol className={cn("flex flex-col", isRefreshing && "opacity-70 transition-opacity")}>
                {rows.map((row) => (
                  <LeaderboardRow key={row.entry.key} row={row} orgSegment={orgSegment} />
                ))}
              </ol>
            ) : null}

            {board ? (
              <p className="mt-4 px-3 text-[12px] leading-5 text-muted-foreground">
                A use is one time a chat in this organization loaded the skill, counted from{" "}
                {new Date(board.window.since).toLocaleDateString(undefined, {
                  day: "numeric",
                  month: "short",
                })}{" "}
                to today.
              </p>
            ) : null}
          </section>
        </div>
      </div>
    </ScrollArea>
  );
}
